import type { SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";

/**
 * Manage infinite scrolling for a list of items by paginating the data on the client side.
 * It allows loading more items as the user scrolls down
 * @param { any[] } items elements array
 * @param { boolean } isOpen condition to reset pagination (ex : when close popup list)
 * @param { number } itemsPerPage expected items number per pages (static value)
 * @returns { paginatedItems, paginatedElRef }
 */

const useInfiniteScroll = (items: any[] | null, isOpen, itemsPerPage = 20) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [paginatedData, setPaginatedData] = useState<any[]>([]);
    const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);

    const handleScroll = useCallback(() => {
        if (!containerNode) return;
        const scrollPercentage = (containerNode.scrollTop + containerNode.clientHeight) / containerNode.scrollHeight;
        if (scrollPercentage > 0.95) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    }, [containerNode]);

    useEffect(() => {
        if (containerNode) {
            containerNode.addEventListener("scroll", handleScroll);
            return () => {
                containerNode.removeEventListener("scroll", handleScroll);
            };
        }
    }, [containerNode, handleScroll]);

    useEffect(() => {
        if (!items) return;
        const newItems = items.slice(0, currentPage * itemsPerPage);
        if (isOpen) {
            setPaginatedData(newItems);
        } else {
            setCurrentPage(1);
        }
    }, [currentPage, items, itemsPerPage, isOpen]);

    const paginatedElRef = useCallback((node: SetStateAction<HTMLDivElement | null>) => {
        if (node !== null) {
            setContainerNode(node);
        }
    }, []);

    return { paginatedData, paginatedElRef };
};

export default useInfiniteScroll;
